import classnames from 'classnames/bind'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useRef } from 'react'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalInputPreset } from '~/@types/input-preset'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextStyling } from '~/@types/text-styling'

import { useOnKeyDown } from '@unlikelystudio/react-hooks'

import { useInputPreset } from '~/components/Abstracts/Form/Input/hooks'
import Input from '~/components/Abstracts/Form/Input/mock'
import { CloseIcon, SearchIcon } from '~/components/Abstracts/Icons'

import { useStyle } from '~/providers/StyleProvider'

import { stringIsNotEmpty } from '~/utils/check-empty-string'
import { getQueryParams } from '~/utils/get-query-params'

import { UrlParamsProps } from '..'
import css from './styles.module.scss'

const cx = classnames.bind(css)

export interface SearchInputProps {
  className?: string
  inputClassName?: string
  isSearching?: boolean
  value: string
  onChange: (value: string) => void
}

function SearchInput({
  className,
  inputClassName,
  isSearching,
  value,
  onChange,
}: SearchInputProps) {
  const inputRef = useRef(null)
  const router = useRouter()

  const inputStyle = useStyle({
    textPreset: GlobalTextPreset.Title12Haffer,
    color: GlobalThemeColors.Black,
    textStyling: GlobalTextStyling.UpperCase,
  })

  const clearSearch = () => {
    onChange && onChange('')
  }

  const params = useMemo<UrlParamsProps>(() => getQueryParams(), [router])
  const { q } = params

  useEffect(() => {
    if (q) inputRef.current.value = q?.replaceAll('+', ' ')
  }, [])

  useOnKeyDown(27, () => {
    if (stringIsNotEmpty(q)) clearSearch()
  })

  const props = useInputPreset(GlobalInputPreset.Search, {})

  return (
    <div className={cx(css.SearchInput, className)}>
      <Input
        {...props}
        className={cx(css.input, inputClassName, inputStyle)}
        onChange={(e) => {
          onChange && onChange(e.currentTarget.value)
        }}
        value={value}
        forwardRef={inputRef}
      />
      {isSearching ? (
        <CloseIcon
          className={cx(css.icon, css.clear)}
          theme={GlobalThemeColors.Black}
          onClick={clearSearch}
        />
      ) : (
        <SearchIcon
          className={cx(css.icon, css.search)}
          theme={GlobalThemeColors.Black}
        />
      )}
    </div>
  )
}

SearchInput.defaultProps = {}

export default SearchInput
